import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';
import LockIconWrapper from '../../components/LockIconWrapper';
import { hasOpenRetainedInsurance } from './UnpaidBookingInvoiceButton';

/**
 * Completes the retained-insurance handling of a CANCELLED booking without sending an
 * unpaid-booking invoice — e.g. because the receivable was already billed manually
 * outside the dedicated flow. Removes the booking from the pending-attention section.
 */
function SkipUnpaidBookingInvoiceButton({
  booking,
  onBookingUpdated,
  onAfterSkip,
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { skipUnpaidBookingInvoice } = useInternalBooking();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id) return false;
    if (booking.status !== 'canceled') return false;
    if (booking.unpaidBookingInvoiceId || booking.unpaidBookingInvoiceSkippedAt) return false;
    if (!hasOpenRetainedInsurance(booking)) return false;
    return authCtx.checkRightAny('internalBooking:special:sendUnpaidBookingInvoice');
  }, [authCtx, booking]);

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isSubmitting) return;
    setOpen(false);
  };

  const handleSubmit = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id) return;

    try {
      setIsSubmitting(true);
      const response = await skipUnpaidBookingInvoice(booking._id);
      if (!response) return;
      if (response?.booking) {
        onBookingUpdated?.(booking._id, response.booking);
      }
      setOpen(false);
      await onAfterSkip?.();
    } catch (err) {
      if (err?.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim Abschließen der offenen Ticketversicherung');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='inherit'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={sx}
      >
        <LockIconWrapper right='internalBooking:special:sendUnpaidBookingInvoice'>
          Keine Rechnung senden (erledigt)
        </LockIconWrapper>
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
        <DialogTitle>Keine Rechnung für die Ticketversicherung senden?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' paragraph>
            Die Buchung verschwindet aus dem Handlungsbedarf. Es wird keine Rechnung
            erstellt oder versendet.
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Nutze diese Option, wenn die Ticketversicherung bereits auf anderem Weg in
            Rechnung gestellt oder kassiert wurde.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Als erledigt markieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SkipUnpaidBookingInvoiceButton;
